<script setup lang="ts">
import { computed } from "vue";
import MarkdownIt from "markdown-it";
const route = useRoute();
const { data } = await useKirbyData(route.path, {
  transform: (page: Record<string, any>) => slimPagePayload(page),
});
requirePageData(data.value);
setPage(data.value);
useArticleJsonLd(computed(() => data.value), () => route.path);

const windowWidth = ref(0); // default value
const isMobile = ref(false);

useHead({
  bodyAttrs: {
    class: `article-page`,
  },
});
definePageMeta({
  layout: "default",
});

// Initialize Markdown-it
const md = new MarkdownIt();

// Function to render markdown
const renderMarkdown = (markdown: string): string => {
  try {
    return md.render(markdown);
  } catch (error) {
    console.error("Markdown rendering error:", error);
    return markdown;
  }
};

// Function to parse old-style links
const parseLinks = (text: string): string => {
  if (!text) return text;

  // Updated regex to parse link syntax without super-linear backtracking
  return text.replace(
    /\(link:\s(\S+)\stext:\s([^)]+)\)/g,
    '<a href="$1">$2</a>',
  );
};

// Reactive data and builder items

const builderItems = computed(
  () =>
    data.value?.blocks?.map((item: any) => ({
      ...item,
      content: {
        ...item.content,
        text: item.content?.text
          ? parseLinks(renderMarkdown(item.content.text))
          : null,
      },
    })) ?? [],
);

function updateWidth() {
  isMobile.value = window.innerWidth <= 991;
  windowWidth.value = window.innerWidth;
}
onMounted(() => {
  isMobile.value = window.innerWidth <= 991;
  // Access the window object only after the component is mounted
  windowWidth.value = window.innerWidth;
  updateWidth();
  window.addEventListener("resize", updateWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});
</script>

<template>
  <div>
    <AppHeading1 />
    <AppHeroImage v-if="data?.heroType === 'Hero Image'" />
    <div
      v-else
      class="main-hero hero"
      :class="{
        'hero-height-small':
          data?.template !== 'home' && data?.template !== 'treks',
      }"
    >
      <div class="main-hero-image">
        <AppSVGBrandGraphic />
        <div>
          <NuxtPicture
            v-if="!isMobile"
            class="hero-image hero-image-default"
            format="webp"
            preload
            src="/images/article-hero-default.jpg"
            width="1920"
            height="663"
            alt="A view of mountains"
            sizes="sm:80vw md:50vw lg:1780px"
          />
          <NuxtPicture
            v-if="isMobile"
            class="hero-image hero-image-default"
            format="webp"
            preload
            src="/images/article-hero-default-mobile.jpg"
            width="530"
            height="663"
            alt="A view of mountains"
            sizes="sm:320px md:430px lg:530px"
          />
        </div>
      </div>
    </div>
    <div class="content-wrapper">
      <div class="content content-article">
        <div class="article-text">
          <h2 v-if="data?.heroType !== 'Hero Image'">{{ data?.title }}</h2>
          <p class="date">{{ data?.published }}</p>
          <KirbyLayouts v-if="data?.layouts" :layouts="data?.layouts ?? []" />
          <div v-if="data?.text" v-html="data?.text" />
          <template v-if="builderItems.length">
            <template
              v-for="(item, index) in builderItems"
              :key="item.id || index"
            >
              <!-- Handle Bodytext Block -->
              <div v-if="item.type === 'bodytext'" v-html="item.content.text" />

              <!-- Handle Page Image Block -->
              <div v-else-if="item.type === 'pageimage'">
                <img
                  v-if="item.content.image && item.content.image[0]?.url"
                  :src="
                    item.content.image[0].url.replace('file://', '/assets/')
                  "
                  :alt="item.content.imagecaption || 'Image'"
                />
                <p v-if="item.content.imagecaption">
                  {{ item.content.imagecaption }}
                </p>
              </div>

              <!-- Handle Intro Text Block -->
              <div
                v-else-if="item.type === 'introtext'"
                v-html="item.content.text"
              />

              <!-- Handle Text and Image Block -->
              <div v-else-if="item.type === 'textimage'">
                <div>
                  <div v-html="item.content.text"></div>
                  <img
                    v-if="item.content.image && item.content.image[0]?.url"
                    :src="
                      item.content.image[0].url.replace('file://', '/assets/')
                    "
                    :alt="item.content.imagecaption || 'Image'"
                  />
                  <p v-if="item.content.imagecaption">
                    {{ item.content.imagecaption }}
                  </p>
                </div>
              </div>

              <!-- Handle Two Column Text Block -->
              <div v-else-if="item.type === 'twocoltext'">
                <div class="two-columns">
                  <div v-html="item.content.text1"></div>
                  <div v-html="item.content.text2"></div>
                </div>
              </div>
            </template>
          </template>

          <AppAuthor />
        </div>
        <section id="moreArticles">
          <h2>More Articles</h2>
          <AppMoreArticlesList />
        </section>
      </div>
    </div>
  </div>
</template>
